import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { getOwner } from '@ember/owner';
import { on } from '@ember/modifier';
import { task } from 'ember-concurrency';
import { macroCondition, isDevelopingApp } from '@embroider/macros';
import { t } from 'ember-intl';
import { or } from 'ember-truth-helpers';
import cannot from 'ember-can/helpers/cannot';
import {
  HdsModal,
  HdsAlert,
  HdsTextBody,
  HdsFormTextInputField,
  HdsButtonSet,
  HdsButton,
} from '@hashicorp/design-system-components/components';
import { Flex, WithErrors } from 'core/components';
import { boxMargin } from 'core/modifiers';
import { ORGANIZATION_UPDATED } from 'core/utils/consts/analytics-events/platform';
import { pick } from '@nullvoxpopuli/ember-composable-helpers';
import set from 'ember-set-helper/helpers/set';

const DEFAULT_NEXT = 'cloud.orgs.list';
const NAME_ALREADY_EXISTS_ERROR =
  'An organization with this name already exists. Enter a different name.';

/**
 *
 * `model` is an object an `organization` and `next` key.
 * @argument model;
 * @type {Object}
 * @required
 *
 */
/**
 *
 * `PageOrganizationsRename` is a modal component for renaming an organization.
 *
 *
 * ```
 * <Page::Organizations::Rename
 *   @model={{hash organization=organization next='cloud.orgs.list'}}
 * />
 * ```
 *
 * @class PageOrganizationsRename
 *
 */
export default class PageOrganizationsRename extends Component {
  @service analytics;
  @service api;
  @service currentUser;
  @service flashMessages;
  @service intl;
  @service router;
  @service userContext;

  @tracked name = this.args?.model?.organization.name;
  @tracked isModalActive = true;

  get nextRoute() {
    let routeName = this.args?.model?.next || DEFAULT_NEXT;
    let route = getOwner(this).lookup(`route:${routeName}`);

    if (!route) {
      route = getOwner(this).lookup(`route:${DEFAULT_NEXT}`);
    }

    return route;
  }

  onClose = () => {
    // prevent the route transition from being called during integration tests
    return !this.isDestroying || !this.isDestroyed
      ? this.router.transitionTo(this.nextRoute.fullRouteName)
      : null;
  };

  async onRenameError(e) {
    const resp = await e.json();

    switch (resp.message) {
      case NAME_ALREADY_EXISTS_ERROR: {
        resp.title = `${this.intl.t(
          'components.page.organizations.rename.error.organization-name-must-be-unique'
        )}.`;
        resp.message = this.intl.t(
          'components.page.organizations.rename.error.the-organization-name-is-already-in-use'
        );
        resp.details = [
          {
            field_violations: [
              {
                field: 'name',
                description: this.intl.t(
                  'components.page.organizations.rename.error.organization-name-must-be-unique'
                ),
              },
            ],
          },
        ];
        break;
      }
    }

    const modifiedResponse = new Response(JSON.stringify(resp), {
      status: e.status,
      statusText: e.statusText,
      headers: e.headers,
    });

    throw modifiedResponse;
  }

  async onRenameSuccess({ organization: updatedOrganization }) {
    let nextRoute = this.nextRoute;

    this.isModalActive = false;

    this.flashMessages.success(
      this.intl.t('components.page.organizations.rename.success')
    );

    // Update the organization list with any updates.
    this.currentUser.organizations = this.currentUser.organizations
      .slice(0)
      .map((organization) => {
        if (organization.id === updatedOrganization.id) {
          return {
            ...organization,
            ...updatedOrganization,
          };
        }

        return organization;
      });

    // Update the current org selected if it's selected.
    if (this.userContext.organization.id === updatedOrganization.id) {
      this.userContext.setOrganization(updatedOrganization);
    }

    this.router.transitionTo(nextRoute.fullRouteName);

    // We should use the router service for this once this PR is merged.
    // via: https://github.com/emberjs/rfcs/issues/592
    nextRoute.refresh();
  }

  save = task(async (evt) => {
    // don't submit the form
    evt.preventDefault();

    const orgId = this.args?.model?.organization?.id;
    const payload = {
      id: orgId,
      name: this.name.trim(),
    };
    let resp;

    this.analytics.trackEvent(ORGANIZATION_UPDATED, payload);

    try {
      resp = await this.api.resourceManager.org.organizationServiceSetName(
        orgId,
        payload
      );

      await this.onRenameSuccess({ ...resp });
    } catch (e) {
      if (macroCondition(isDevelopingApp())) {
        console.error(e);
      }
      await this.onRenameError(e);
    }
  });

  <template>
    {{#if this.isModalActive}}
      <WithErrors @error={{this.save.last.error}} as |FormError FieldError|>
        <HdsModal
          class="organizationRenameModal"
          @color="warning"
          @onClose={{this.onClose}}
          as |M|
        >
          <M.Header
            @icon="alert-triangle"
            @tagline={{@model.organization.name}}
          >
            {{t "components.page.organizations.rename.title"}}
          </M.Header>
          <M.Body>
            <Flex @direction="column" @gap="md" {{boxMargin "0 0 md 0"}}>
              <HdsAlert
                data-test-org-rename-alert
                @type="inline"
                @color="warning"
                as |A|
              >
                <A.Title>
                  {{t "components.page.organizations.rename.alert.title"}}
                </A.Title>
                <A.Description>
                  {{t "components.page.organizations.rename.alert.description"}}
                </A.Description>
                <A.LinkStandalone
                  @color="secondary"
                  @icon="docs-link"
                  @text={{t
                    "components.page.organizations.rename.alert.link.text"
                  }}
                  @href={{t
                    "components.page.organizations.rename.alert.link.href"
                  }}
                />
              </HdsAlert>
              {{#if this.save.last.error}}
                <FormError
                  @shouldRenderPageError={{true}}
                  data-test-organization-rename-form-error
                />
              {{/if}}
              <HdsTextBody
                @color="primary"
                @size="200"
                @tag="p"
                @weight="regular"
              >
                {{t "components.page.organizations.rename.form.description"}}
              </HdsTextBody>
            </Flex>
            <form
              id="org-rename"
              class="hcp-hds-form"
              data-test-org-rename-form
              {{on "submit" this.save.perform}}
            >
              <FieldError @field="name" @hdsDelegatedError={{true}} as |err|>
                <HdsFormTextInputField
                  @id="org-new-name"
                  @type="text"
                  @value={{this.name}}
                  name="org-new-name"
                  data-test-org-new-name
                  {{on "input" (pick "target.value" (set this "name"))}}
                  as |F|
                >
                  <F.HelperText>
                    <ul class="input-requirements">
                      <li>
                        {{t
                          "components.page.organizations.create.form.name.description.must-be-unique"
                        }}
                      </li>
                      <li>
                        {{t
                          "components.page.organizations.create.form.name.description.may-contain"
                        }}
                      </li>
                    </ul>
                  </F.HelperText>
                  <F.Label>
                    {{t "components.page.organizations.rename.form.name.label"}}
                  </F.Label>
                  <F.CharacterCount @minLength={{3}} @maxLength={{40}} />
                  {{#if err}}
                    <F.Error>
                      {{err.description}}
                    </F.Error>
                  {{/if}}
                </HdsFormTextInputField>
              </FieldError>
            </form>
          </M.Body>
          <M.Footer as |F|>
            <HdsButtonSet>
              <HdsButton
                @color="primary"
                @text={{t "components.form.save"}}
                disabled={{or
                  (cannot "rename organization")
                  this.save.isRunning
                }}
                form="org-rename"
                type="submit"
                data-test-org-rename-submit
              />
              <HdsButton
                data-test-org-rename-cancel
                type="button"
                @text={{t "components.form.cancel"}}
                @color="secondary"
                {{on "click" F.close}}
              />
            </HdsButtonSet>
          </M.Footer>
        </HdsModal>
      </WithErrors>
    {{/if}}
  </template>
}
